// The "Super Admin" and "Admin" roles should only ever be selectable/visible
// in the Role picker (Create User / Edit User) to accounts allowed to grant
// them — everyone else must not see them as options. This is a UI
// convenience filter only, not a security boundary: the real access control
// is the server's RBAC, so hiding it here just prevents accidentally
// assigning/seeing it, it doesn't block the request itself.
//
// roleName is free text (created via POST /api/role, not an enum), so match
// loosely — case/space/underscore-insensitive — rather than one exact string.
const normalizeRoleName = (roleName) => (roleName || "").replace(/[\s_]+/g, "").toLowerCase();
const isSuperAdminRoleName = (roleName) => normalizeRoleName(roleName) === "superadmin";
const isAdminRoleName = (roleName) => normalizeRoleName(roleName) === "admin";

// The logged-in user's own id, stored at login (see views/auth/signIn) —
// id 1 is the seeded super admin account (see UserSeeder.js).
export const getCurrentUserId = () => Number(sessionStorage.getItem("user_id"));
export const isCurrentUserSuperAdmin = () => getCurrentUserId() === 1;

// The logged-in user's own role name, from the `permissions` blob stored at
// login (views/auth/signIn/index.jsx: `{...role, permissions}`, and `role`
// carries `roleName`).
export const getCurrentUserRoleName = () => {
  try {
    return JSON.parse(sessionStorage.getItem("permissions") || "{}").roleName || "";
  } catch {
    return "";
  }
};
export const isCurrentUserAdmin = () => isAdminRoleName(getCurrentUserRoleName());

// Only Admins and the Super Admin may assign/see the Admin role; only the
// Super Admin may assign/see the Super Admin role.
export const visibleRolesFor = (roles) => {
  const canSeeSuperAdmin = isCurrentUserSuperAdmin();
  const canSeeAdmin = isCurrentUserAdmin() || canSeeSuperAdmin;
  return (roles || []).filter((role) => {
    if (isSuperAdminRoleName(role.roleName)) return canSeeSuperAdmin;
    if (isAdminRoleName(role.roleName)) return canSeeAdmin;
    return true;
  });
};

// Only Admins and the Super Admin may change another user's role at all
// (Editors etc. can't reassign anyone's role, even to a lower one).
export const canEditUserRole = () => isCurrentUserAdmin() || isCurrentUserSuperAdmin();
